/**
 * Saving a card (2.M deliverable 9, pure): what a save sends, how the answer is adopted and how
 * the card writes' refusals read. The server owns `data.source`, so an update carries the stored
 * one and a create sends the form's.
 */
import {
  ApiError,
  type CARD_WRITE_ISSUES,
  type RulesCreateV1,
  type RulesRecordV1,
  type RulesUpdateV1,
} from '@codex/contracts';
import type { CardState } from './draft.js';

export type SavePlan =
  | { readonly kind: 'create'; readonly body: RulesCreateV1 }
  | { readonly kind: 'update'; readonly body: RulesUpdateV1 }
  | { readonly kind: 'none' };

/** Structural equality of JSON values (key order does not matter). */
export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every(
    (key) =>
      Object.hasOwn(b, key) &&
      sameJson((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
}

export function stateFromCard(card: RulesRecordV1): CardState {
  return card.kind === 'spell'
    ? { kind: 'spell', data: card.data, filled: 'form' }
    : { kind: 'item', data: card.data, filled: 'form' };
}

/** `create` without a saved card, `update` when the data changed, else `none`. */
export function savePlan(state: CardState, saved?: RulesRecordV1 | null): SavePlan {
  if (!saved) {
    return { kind: 'create', body: { kind: state.kind, data: state.data, source: 'form' } };
  }
  const data = { ...state.data, source: saved.data.source };
  if (sameJson(data, saved.data)) return { kind: 'none' };
  return { kind: 'update', body: { baseRv: saved.rv, data } };
}

/** After a create or update, the saved card and the draft are the answer's card. */
export function adoptSaved(
  state: CardState,
  answer: RulesRecordV1,
): { readonly state: CardState; readonly saved: RulesRecordV1 } {
  return { state: { ...stateFromCard(answer), filled: state.filled }, saved: answer };
}

export type SaveError =
  | { readonly kind: 'conflict' }
  | { readonly kind: 'message'; readonly text: string };

export const TRASHED_TEXT = 'This page is in the trash. Restore it to edit its card.';

const ISSUE_TEXT: Readonly<Record<(typeof CARD_WRITE_ISSUES)[number], string>> = {
  type_without_card: "This page's type doesn't have cards.",
  kind_mismatch: "This card doesn't match the page's type.",
  rules_2024: 'Only 2014 cards can be saved.',
  schema_invalid: 'Some fields hold values a card cannot store.',
  unknown_type: "This page's type no longer exists.",
};

const GENERIC = "Couldn't save the card.";

/** The card writes' answers as the form shows them. */
export function errorMessage(error: unknown): SaveError {
  if (!(error instanceof ApiError)) return { kind: 'message', text: GENERIC };
  if (error.status === 409 && error.code === 'row_conflict') return { kind: 'conflict' };
  if (error.status === 409 && error.code === 'document_trashed') {
    return { kind: 'message', text: TRASHED_TEXT };
  }
  if (error.status === 422 && error.code === 'invalid_body') {
    const details = error.body.details;
    const issue =
      typeof details === 'object' && details !== null
        ? (details as { issue?: unknown }).issue
        : undefined;
    const text =
      typeof issue === 'string' ? ISSUE_TEXT[issue as keyof typeof ISSUE_TEXT] : undefined;
    return { kind: 'message', text: text ?? GENERIC };
  }
  if (error.status === 403 && error.code === 'reference_read_only') {
    return { kind: 'message', text: "SRD entries can't be edited." };
  }
  if (error.status === 404) return { kind: 'message', text: 'This page no longer exists.' };
  return { kind: 'message', text: GENERIC };
}
